/**
 * Upload Documents Hook
 *
 * Orchestrates file uploads using Convex storage, backed by a zustand store
 * for persistent session state across sheet open/close cycles.
 */

import { useCallback, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";
import { useWorkspaceId } from "@/hooks";
import {
  useUploadSessionStore,
  type UploadSessionFile,
} from "@/stores/upload-session-store";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface UploadFile {
  id: string;
  originalName: string;
  displayName: string;
  fileType: string;
  tags: string[];
  categoryId?: Id<"documentCategories">;
  expirationDate?: number;
  progress: number;
  /** The raw File object — needed for actual upload */
  _file?: File;
}

export type UploadPhase =
  | "idle"
  | "assigning"
  | "uploading"
  | "processing"
  | "completed";

export type DuplicateDocumentMatch = {
  fileName: string;
  existingDocumentId: Id<"documents">;
  uploadedAt: number;
  uploadedByName: string;
  summaryPreview: string | null;
};

export type ConflictResolution =
  | { type: "new-version"; existingDocumentId: Id<"documents"> }
  | { type: "keep-both" }
  | { type: "rename"; newFileName: string };

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const MAX_FILES = 10;
let fileIdCounter = 0;

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "svg", "webp"];
const KNOWN_EXTS = ["pdf", "docx", "xlsx", "pptx", "txt"];

function getFileType(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  if (IMAGE_EXTS.includes(ext)) return "image";
  if (KNOWN_EXTS.includes(ext)) return ext;
  return "txt";
}

/**
 * Map from local UploadFile to session store's UploadSessionFile
 */
function toSessionFile(file: UploadFile): UploadSessionFile {
  return {
    localId: file.id,
    fileName: file.displayName,
    fileType: file.fileType,
    tags: file.tags,
    categoryId: file.categoryId,
    uploadStatus: "pending",
    uploadProgress: 0,
  };
}

/* ------------------------------------------------------------------ */
/* In-memory file map (raw File objects can't go in zustand)           */
/* ------------------------------------------------------------------ */

const rawFileMap = new Map<string, File>();

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

export function useUploadDocuments() {
  const store = useUploadSessionStore();
  const workspaceId = useWorkspaceId();
  const abortRef = useRef(false);

  // Local state for UploadFile enrichment (tags, displayName, raw File)
  // These are not persisted in zustand since they only matter pre-upload
  const localFilesRef = useRef<Map<string, UploadFile>>(new Map());

  // Conflict resolutions — stored in ref so uploadSingleFile always reads latest
  const conflictResolutionsRef = useRef<Map<string, ConflictResolution>>(
    new Map(),
  );

  const generateUploadUrl = useMutation(
    api.document_mutations.generateUploadUrl,
  );
  const createDocumentMutation = useApiMutation(api.document_mutations.create, {
    name: "create-document",
    silentSuccess: true,
  });
  const uploadNewVersionMutation = useApiMutation(
    api.document_mutations.uploadNewVersion,
    { name: "upload-new-version", silentSuccess: true },
  );
  // Clear stale sessions on mount
  store.clearStaleSession();

  /* -- Duplicate detection -- */

  const fileNames = store.files.map((f) => f.fileName);
  const duplicateMatches = useQuery(
    api.documents.findDuplicatesByFileName,
    fileNames.length > 0 ? { fileNames, workspaceId } : "skip",
  );
  // Fail open: treat undefined (loading/error) as no conflicts
  const conflicts: DuplicateDocumentMatch[] = duplicateMatches ?? [];

  // For each conflicting file name, find the next available numbered variant
  const conflictFileNames = conflicts.map((c) => c.fileName);
  const nextAvailableNamesResult = useQuery(
    api.documents.getNextAvailableNames,
    conflictFileNames.length > 0
      ? { fileNames: conflictFileNames, workspaceId }
      : "skip",
  );
  const nextAvailableNames: Record<string, string> =
    nextAvailableNamesResult ?? {};
  const isCheckingDuplicates =
    fileNames.length > 0 && duplicateMatches === undefined;

  const resolveConflicts = useCallback(
    (resolutions: Map<string, ConflictResolution>) => {
      for (const [key, value] of resolutions) {
        conflictResolutionsRef.current.set(key, value);
        if (value.type === "rename") {
          // Update displayName so the file row shows the new name after resolution
          for (const [id, local] of localFilesRef.current) {
            if (local.originalName === key) {
              localFilesRef.current.set(id, {
                ...local,
                displayName: value.newFileName,
              });
              store.renameFile(id, value.newFileName);
              break;
            }
          }
        }
      }
    },
    [store],
  );

  /* -- File management -- */

  const addFiles = useCallback(
    (newFiles: File[]): number => {
      const remaining = MAX_FILES - store.files.length;
      const toAdd = newFiles.slice(0, remaining);
      const overflow = newFiles.length - toAdd.length;

      if (toAdd.length > 0) {
        const uploadFiles: UploadFile[] = toAdd.map((f) => ({
          id: `upload-${++fileIdCounter}`,
          originalName: f.name,
          displayName: f.name,
          fileType: getFileType(f.name),
          tags: [],
          progress: 0,
          _file: f,
        }));

        // Store raw files in local ref map
        for (const uf of uploadFiles) {
          if (uf._file) {
            rawFileMap.set(uf.id, uf._file);
          }
          localFilesRef.current.set(uf.id, uf);
        }

        // Add to zustand store
        store.addFiles(uploadFiles.map(toSessionFile));
      }

      return overflow;
    },
    [store],
  );

  const removeFile = useCallback(
    (id: string) => {
      const local = localFilesRef.current.get(id);
      if (local) {
        conflictResolutionsRef.current.delete(local.originalName);
      }
      rawFileMap.delete(id);
      localFilesRef.current.delete(id);
      store.removeFile(id);
    },
    [store],
  );

  const renameFile = useCallback(
    (id: string, name: string) => {
      const local = localFilesRef.current.get(id);
      if (local) {
        localFilesRef.current.set(id, { ...local, displayName: name });
      }
      // Update store so duplicate-detection re-runs with the new name
      store.renameFile(id, name);
    },
    [store],
  );

  const addTag = useCallback(
    (fileId: string, tagId: string) => {
      store.addFileTag(fileId, tagId);
    },
    [store],
  );

  const removeTag = useCallback(
    (fileId: string, tagId: string) => {
      store.removeFileTag(fileId, tagId);
    },
    [store],
  );

  const setFileCategoryId = useCallback(
    (fileId: string, categoryId: Id<"documentCategories">) => {
      const local = localFilesRef.current.get(fileId);
      if (local) {
        localFilesRef.current.set(fileId, {
          ...local,
          categoryId,
        });
      }
      store.setFileCategoryId(fileId, categoryId);
    },
    [store],
  );

  const setFileExpirationDate = useCallback(
    (fileId: string, date: number | undefined) => {
      const local = localFilesRef.current.get(fileId);
      if (local) {
        localFilesRef.current.set(fileId, {
          ...local,
          expirationDate: date,
        });
      }
      store.setFileExpirationDate(fileId, date);
    },
    [store],
  );

  /* -- Upload logic -- */

  const uploadSingleFile = useCallback(
    async (fileId: string) => {
      const rawFile = rawFileMap.get(fileId);
      const local = localFilesRef.current.get(fileId);
      if (!rawFile || !local) return;

      try {
        store.setFileUploadStatus(fileId, "uploading", 10);

        // Step 1: get upload URL
        const uploadUrl = await generateUploadUrl({});

        // Step 2: upload file to Convex storage
        const uploadResult = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": rawFile.type },
          body: rawFile,
        });

        if (!uploadResult.ok) {
          throw new Error(`Upload failed: ${uploadResult.statusText}`);
        }

        const { storageId } = (await uploadResult.json()) as {
          storageId: Id<"_storage">;
        };

        // Step 3: mark progress at 80% (upload done, creating record)
        store.setFileUploadStatus(fileId, "uploading", 80);

        // Step 4: apply conflict resolution if one exists for this file
        const resolution = conflictResolutionsRef.current.get(
          local.originalName,
        );

        if (resolution?.type === "new-version") {
          // Upload as new version of existing document
          await uploadNewVersionMutation.mutateAsync({
            documentId: resolution.existingDocumentId,
            storageId,
            fileName: local.displayName,
            workspaceId,
          });
          store.setFileDocumentId(fileId, resolution.existingDocumentId);
          store.setFileUploadStatus(fileId, "done", 100);
          return;
        }

        // Determine file name — rename resolution overrides display name
        const uploadFileName =
          resolution?.type === "rename"
            ? resolution.newFileName
            : local.displayName;

        // Step 5: create document record with storage reference
        const storeFile = store.files.find((f) => f.localId === fileId);
        const tagIds = (storeFile?.tags ??
          []) as unknown as Id<"documentTags">[];

        const result = await createDocumentMutation.mutateAsync({
          fileName: uploadFileName,
          fileType: local.fileType === "image" ? "png" : local.fileType,
          storageId,
          tags: tagIds.length > 0 ? tagIds : undefined,
          auditContextIds:
            store.selectedAuditIds.length > 0
              ? store.selectedAuditIds
              : undefined,
          categoryId: local.categoryId,
          expirationDate: local.expirationDate,
          workspaceId,
        } as never);

        // Step 6: capture documentId and mark complete
        // useApiMutation unwraps Result at runtime — the returned data is Doc<"documents">
        const doc = result as unknown as { _id: Id<"documents"> };
        store.setFileDocumentId(fileId, doc._id);
        store.setFileUploadStatus(fileId, "done", 100);
      } catch {
        store.setFileUploadStatus(fileId, "error", 0);
      }
    },
    [
      generateUploadUrl,
      createDocumentMutation,
      uploadNewVersionMutation,
      store,
      workspaceId,
    ],
  );

  const startUpload = useCallback(async () => {
    store.setPhase("uploading");
    abortRef.current = false;

    const fileIds = store.files
      .filter((f) => rawFileMap.has(f.localId))
      .map((f) => f.localId);

    await Promise.all(
      fileIds.map(async (fileId, index) => {
        // Stagger start by 200ms per file
        if (index > 0) {
          await new Promise((r) => setTimeout(r, index * 200));
        }
        if (abortRef.current) return;
        await uploadSingleFile(fileId);
      }),
    );

    if (!abortRef.current) {
      // Transition to processing instead of closing
      store.setPhase("processing");
    }
  }, [store, uploadSingleFile]);

  const reset = useCallback(() => {
    abortRef.current = true;
    rawFileMap.clear();
    localFilesRef.current.clear();
    conflictResolutionsRef.current = new Map();
    store.reset();
  }, [store]);

  // Build UploadFile[] for UI consumption (merge store state with local metadata)
  const files: UploadFile[] = store.files.map((sf) => {
    const local = localFilesRef.current.get(sf.localId);
    return {
      id: sf.localId,
      originalName: local?.originalName ?? sf.fileName,
      displayName: local?.displayName ?? sf.fileName,
      fileType: sf.fileType,
      tags: sf.tags,
      categoryId: local?.categoryId ?? sf.categoryId,
      expirationDate: local?.expirationDate ?? sf.expirationDate,
      progress: sf.uploadProgress,
      _file: rawFileMap.get(sf.localId),
    };
  });

  // Map store phase to legacy UploadPhase for compatibility
  const phase: UploadPhase =
    store.phase === "selecting"
      ? "idle"
      : store.phase === "idle"
        ? "idle"
        : store.phase;

  const hasInteracted = store.files.length > 0 || store.phase !== "idle";

  return {
    files,
    phase,
    hasInteracted,
    maxFiles: MAX_FILES,
    canAddMore: store.files.length < MAX_FILES,
    addFiles,
    removeFile,
    renameFile,
    addTag,
    removeTag,
    setFileCategoryId,
    setFileExpirationDate,
    startUpload,
    setPhase: store.setPhase,
    reset,
    // Session store access for processing view
    sessionFiles: store.files,
    // Helper to check if all files have categories
    allFilesHaveCategory: files.every((f) => f.categoryId != null),
    filesWithCategory: files.filter((f) => f.categoryId != null).length,
    // Duplicate detection
    conflicts,
    isCheckingDuplicates,
    resolveConflicts,
    nextAvailableNames,
  };
}
